import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { academicYearService } from '../services/api/academicYear';
import {
  AcademicYear,
  AcademicYearFormData,
  CloneClassData,
  CreateAcademicYearRequest,
  UpdateAcademicYearRequest,
  Term,
} from '../types/academicYear';
import toast from 'react-hot-toast';
import { useAcademicYearStore } from '../store/academicYearStore';

export const useAcademicYear = (instituteId: number) => {
  const queryClient = useQueryClient();
  const { setDefaultAcademicYear } = useAcademicYearStore();

  const queryKeys = {
    academicYears: (instituteId: number) => ['academicYears', instituteId],
    academicYearById: (instituteId: number, id: number) => ['academicYear', instituteId, id],
    activeAcademicYear: (instituteId: number) => ['academicYear', 'active', instituteId],
  };

  const academicYears = useQuery({
    queryKey: queryKeys.academicYears(instituteId),
    queryFn: () => academicYearService.getAll(instituteId),
    enabled: !!instituteId,
    staleTime: 5 * 60 * 1000,
  });

  const activeAcademicYear = useQuery({
    queryKey: queryKeys.activeAcademicYear(instituteId),
    queryFn: () => academicYearService.getActive(instituteId),
    enabled: !!instituteId,
    staleTime: 5 * 60 * 1000,
  });

  const useGetAcademicYear = (id: number) =>
    useQuery({
      queryKey: queryKeys.academicYearById(instituteId, id),
      queryFn: () => academicYearService.getById(id, instituteId),
      enabled: !!id && !!instituteId,
      staleTime: 5 * 60 * 1000,
    });

  const createAcademicYear = useMutation({
    mutationFn: (data: AcademicYearFormData) => {
      const request: CreateAcademicYearRequest = {
        instituteId,
        name: data.name,
        displayName: data.displayName,
        startDate: data.startDate,
        endDate: data.endDate,
        terms: data.terms,
      };
      return academicYearService.create(request);
    },
    onSuccess: () => {
      toast.success('Academic year created successfully');
      queryClient.invalidateQueries({ queryKey: queryKeys.academicYears(instituteId) });
    },
    onError: (error) => {
      console.error('Error creating academic year:', error);
    },
  });

  const updateAcademicYear = useMutation({
    mutationFn: ({ id, data }: { id: number; data: AcademicYearFormData & { deleteTermIds?: number[] } }) => {
      const request: UpdateAcademicYearRequest = {
        id,
        name: data.name,
        displayName: data.displayName,
        startDate: data.startDate,
        endDate: data.endDate,
        terms: data.terms.map((t, idx) => ({
          ...t,
          id: (t as Term & { id?: number }).id,
          sequence: t.sequence || idx + 1,
        })),
        deleteTermIds: data.deleteTermIds,
        status: data.status,
      };
      return academicYearService.update(request);
    },
    onSuccess: (_, variables) => {
      toast.success('Academic year updated successfully');
      queryClient.invalidateQueries({ queryKey: queryKeys.academicYears(instituteId) });
      queryClient.invalidateQueries({
        queryKey: queryKeys.academicYearById(instituteId, variables.id),
      });
    },
    onError: (error) => {
      console.error('Error updating academic year:', error);
    },
  });

  const setDefaultYear = useMutation({
    mutationFn: (id: number) => academicYearService.setDefault(id, instituteId),
    onSuccess: (_, id) => {
      toast.success('Default academic year updated');
      // Get the year name from cache so the store can auto-switch selectedAcademicYearId
      const cached = queryClient.getQueryData<AcademicYear[]>(queryKeys.academicYears(instituteId));
      const yearName = cached?.find(y => y.id === id)?.name;
      setDefaultAcademicYear(id, yearName);
      queryClient.invalidateQueries({ queryKey: queryKeys.academicYears(instituteId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.activeAcademicYear(instituteId) });
    },
    onError: (error) => {
      console.error('Error setting default academic year:', error);
    },
  });

  const activateAcademicYear = useMutation({
    mutationFn: (id: number) => academicYearService.activate(id),
    onSuccess: (_, id) => {
      toast.success('Academic year activated');
      queryClient.invalidateQueries({ queryKey: queryKeys.academicYears(instituteId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.academicYearById(instituteId, id) });
    },
    onError: (error) => {
      console.error('Error activating academic year:', error);
    },
  });

  const completeAcademicYear = useMutation({
    mutationFn: (id: number) => academicYearService.complete(id),
    onSuccess: (_, id) => {
      toast.success('Academic year marked as completed');
      queryClient.invalidateQueries({ queryKey: queryKeys.academicYears(instituteId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.academicYearById(instituteId, id) });
    },
    onError: (error) => {
      console.error('Error completing academic year:', error);
    },
  });

  const archiveAcademicYear = useMutation({
    mutationFn: (id: number) => academicYearService.archive(id),
    onSuccess: (_, id) => {
      toast.success('Academic year archived');
      queryClient.invalidateQueries({ queryKey: queryKeys.academicYears(instituteId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.academicYearById(instituteId, id) });
    },
    onError: (error) => {
      console.error('Error archiving academic year:', error);
    },
  });

  const deleteAcademicYear = useMutation({
    mutationFn: (id: number) => academicYearService.delete(id),
    onSuccess: (_, id) => {
      toast.success('Academic year deleted');
      queryClient.invalidateQueries({ queryKey: queryKeys.academicYears(instituteId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.academicYearById(instituteId, id) });
    },
    onError: (error) => {
      console.error('Error deleting academic year:', error);
    },
  });

  const invalidateAcademicYears = () => {
    return queryClient.invalidateQueries({ queryKey: queryKeys.academicYears(instituteId) });
  };

  const cloneClassData = useMutation({
    mutationFn: ({ yearId, classes }: { yearId: number; classes: CloneClassData[] }) =>
      academicYearService.cloneClassData(yearId, classes),
    onError: (error) => {
      console.error('Error cloning class data:', error);
    },
  });

  return {
    academicYears: academicYears.data ?? [],
    activeAcademicYear: activeAcademicYear.data,
    isLoading: academicYears.isLoading,
    isLoadingActive: activeAcademicYear.isLoading,
    error: academicYears.error,
    isFetched: academicYears.isFetched,
    useGetAcademicYear,
    createAcademicYear,
    updateAcademicYear,
    setDefaultYear,
    activateAcademicYear,
    completeAcademicYear,
    archiveAcademicYear,
    deleteAcademicYear,
    invalidateAcademicYears,
    cloneClassData,
  };
};
